import { useCallback } from 'react';

import { useUrlResolver } from './useUrlResolver';

/**
 * Resolves the post-purchase redirect for the full-page checkout flow. Thin wrapper
 * over the canonical resolver (`checkout_page` context):
 *   - `getCheckoutCompleteUrl` returns the destination URL, or `null` when the service
 *     has no configured redirect — preserving the original null-on-miss contract.
 *   - `handleCheckoutComplete` navigates there, or reloads the page to refresh
 *     entitlements when there is no URL.
 *
 * The service-code → route config lives in `serviceRoutes.js#checkout_page`. Its two
 * consumers (OrderTracker, CTAButton) depend on this exact surface.
 *
 * @returns {{ getCheckoutCompleteUrl: Function, handleCheckoutComplete: Function }}
 */
export default function usePageCheckoutComplete() {
  const resolve = useUrlResolver();

  const getCheckoutCompleteUrl = useCallback(
    (serviceCode, orderUuid = null) => {
      const { url, found } = resolve(serviceCode, { context: 'checkout_page', orderUuid });

      return found ? url : null;
    },
    [resolve],
  );

  const handleCheckoutComplete = useCallback(
    (serviceCode, orderUuid = null) => {
      const url = getCheckoutCompleteUrl(serviceCode, orderUuid);

      if (!url) {
        // Default behavior: reload the page to refresh entitlements
        window.location.reload();
        return;
      }

      window.location.href = url;
    },
    [getCheckoutCompleteUrl],
  );

  return {
    handleCheckoutComplete,
    getCheckoutCompleteUrl,
  };
}
